import { useState, useEffect } from 'react';

const WeatherWidget = () => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_KEY = import.meta.env.VITE_WEATHER_API_KEY || 'bf492e7ecda0495697620935251509';
  const LOCATION = '-41.2033446,146.8126933'; // Beaconsfield, Tasmania coordinates

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
          `https://api.weatherapi.com/v1/forecast.json?key=${API_KEY}&q=${LOCATION}&days=3&aqi=no&alerts=no`
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch weather data');
        }
        
        const data = await response.json();
        
        // Save to localStorage with timestamp
        const weatherCache = {
          data: data,
          timestamp: Date.now()
        };
        localStorage.setItem('weatherData', JSON.stringify(weatherCache));
        
        setWeatherData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const loadWeatherData = () => {
      // Check if we have cached data
      const cachedData = localStorage.getItem('weatherData');
      
      if (cachedData) {
        try {
          const { data, timestamp } = JSON.parse(cachedData);
          const now = Date.now();
          const twelveHours = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
          
          // If data is less than 12 hours old, use cached data
          if (now - timestamp < twelveHours) {
            setWeatherData(data);
            setLoading(false);
            return;
          }
        } catch (err) {
          // If parsing fails, remove corrupted cache
          localStorage.removeItem('weatherData');
        }
      }
      
      // If no valid cache, fetch new data
      fetchWeather();
    };

    loadWeatherData();
    
    // Check for updates every hour (but only fetch if 12 hours have passed)
    const interval = setInterval(loadWeatherData, 60 * 60 * 1000); // Check every hour
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="weather-widget">
        <div className="weather-loading">
          <div className="weather-loading-text">Loading weather...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-widget">
        <div className="weather-error">
          <div className="weather-error-text">Unable to load weather data</div>
        </div>
      </div>
    );
  }

  if (!weatherData) return null;

  const { current, forecast } = weatherData;
  const today = forecast.forecastday[0];
  const tomorrow = forecast.forecastday[1];
  const dayAfter = forecast.forecastday[2];

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="weather-widget">
      <div className="weather-widget-header">
        <h3 className="weather-widget-title">
          Weather Forecast - Beaconsfield
        </h3>
        <div className="weather-widget-timestamp">
          Updated: {new Date().toLocaleTimeString('en-AU', { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>
      
      <div className="weather-widget-grid">
        {/* Today */}
        <div className="weather-card weather-card-today">
          <div className="weather-card-content">
            <div className="weather-card-date weather-card-date-today">{formatDate(today.date)}</div>
            <div className="weather-card-condition weather-card-condition-today">{today.day.condition.text}</div>
            <div className="weather-card-temp-range weather-card-temp-range-today">
              {Math.round(today.day.mintemp_c)}° - {Math.round(today.day.maxtemp_c)}°C
            </div>
          </div>
          <div className="weather-card-icon-section">
            <img 
              src={`https:${today.day.condition.icon}`} 
              alt={today.day.condition.text}
              className="weather-card-icon"
            />
            <div className="weather-card-temp weather-card-temp-today">
              {Math.round(current.temp_c)}°C
            </div>
          </div>
        </div>

        {/* Tomorrow */}
        <div className="weather-card weather-card-future">
          <div className="weather-card-content">
            <div className="weather-card-date weather-card-date-future">{formatDate(tomorrow.date)}</div>
            <div className="weather-card-condition weather-card-condition-future">{tomorrow.day.condition.text}</div>
            <div className="weather-card-temp-range weather-card-temp-range-future">
              {Math.round(tomorrow.day.mintemp_c)}° - {Math.round(tomorrow.day.maxtemp_c)}°C
            </div>
          </div>
          <div className="weather-card-icon-section">
            <img 
              src={`https:${tomorrow.day.condition.icon}`} 
              alt={tomorrow.day.condition.text}
              className="weather-card-icon"
            />
            <div className="weather-card-temp weather-card-temp-future">
              {Math.round(tomorrow.day.maxtemp_c)}°C
            </div>
          </div>
        </div>

        {/* Day After Tomorrow */}
        <div className="weather-card weather-card-future">
          <div className="weather-card-content">
            <div className="weather-card-date weather-card-date-future">{formatDate(dayAfter.date)}</div>
            <div className="weather-card-condition weather-card-condition-future">{dayAfter.day.condition.text}</div>
            <div className="weather-card-temp-range weather-card-temp-range-future">
              {Math.round(dayAfter.day.mintemp_c)}° - {Math.round(dayAfter.day.maxtemp_c)}°C
            </div>
          </div>
          <div className="weather-card-icon-section">
            <img 
              src={`https:${dayAfter.day.condition.icon}`} 
              alt={dayAfter.day.condition.text}
              className="weather-card-icon"
            />
            <div className="weather-card-temp weather-card-temp-future">
              {Math.round(dayAfter.day.maxtemp_c)}°C
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;
